import {
	ActionButton,
	ConfirmModal,
	FilterChip,
	ListPageChartCard,
	ListPageCharts,
	ListPageLayout,
	Pagination,
	PromotionCard,
	SectionCard,
	PeriodPanel,
	useConfirmModal,
	useToast,
} from '@practium/ui';
import type { CustomPanelControls, SectionTab } from '@practium/ui';
import { useCallback, useEffect } from 'react';
import { useDeleteLog } from '@practium/features/api';
import { useLogsPage } from '../hooks/useLogsPage';
import { LogsTable } from '../components/Tables/LogsTable/LogsTable';
import { LogDetailModal } from '../components/Modals/LogDetailModal/LogDetailModal';
import { SystemStatusCard } from '../components/Cards/SystemStatusCard/SystemStatusCard';
import { RequestsCard } from '../components/Cards/RequestsCard/RequestsCard';
import { LogsPageSkeleton } from '../components/Skeletons/LogsPageSkeleton';
import type { LogView } from '../types/log.types';
import { CreatedAtUntilPanel } from '../components/Filters/CreatedAtUntilPanel';
import { UserAuthorPanel } from '../components/Filters/UserAuthorPanel';
import { useTheme } from '@practium/features/theme';
import styles from './LogsPage.module.css';

export const LogsPage = () => {
	const {
		logs,
		isHealthy,
		isMetadataLoading,
		criticalErrorsThisMonth,
		isLoading,
		isFetching,
		searchQuery,
		setSearchQuery,
		filterValues,
		setFilterValues,
		filterGroups,
		customDateFrom,
		customDateTo,
		applyCustomPeriodFilter,
		createdAtUntil,
		applyCreatedAtUntilFilter,
		selectedAuthorId,
		selectedAuthor,
		selectAuthorFilter,
		page,
		setPage,
		totalPages,
		selectedLog,
		setSelectedLog,
	} = useLogsPage();
	const { changeTheme } = useTheme();
	const { mutateAsync: deleteLog, isPending: isDeletingLog } = useDeleteLog();
	const { success: toastSuccess, error: toastError } = useToast();
	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	useEffect(() => {
		if (typeof isHealthy !== 'boolean') return;
		changeTheme(isHealthy ? 'orvalho' : 'cereja');
	}, [changeTheme, isHealthy]);

	const renderPeriodRangePanel = useCallback(
		({ close, activate }: CustomPanelControls) => (
			<PeriodPanel
				dateFrom={customDateFrom}
				dateTo={customDateTo}
				onChange={(from, to) => {
					applyCustomPeriodFilter(from, to);
					activate();
				}}
				onClose={close}
			/>
		),
		[applyCustomPeriodFilter, customDateFrom, customDateTo]
	);

	const renderCreatedAtUntilPanel = useCallback(
		({ close, activate }: CustomPanelControls) => (
			<CreatedAtUntilPanel
				value={createdAtUntil}
				onChange={(value) => {
					applyCreatedAtUntilFilter(value);
					activate();
				}}
				onClose={close}
			/>
		),
		[applyCreatedAtUntilFilter, createdAtUntil]
	);

	const renderUserAuthorPanel = useCallback(
		({ close, activate, deactivate }: CustomPanelControls) => (
			<UserAuthorPanel
				selectedAuthorId={selectedAuthorId}
				selectedAuthor={selectedAuthor}
				onSelect={(author) => {
					selectAuthorFilter(author);
					activate();
				}}
				onClear={() => {
					deactivate();
					close();
				}}
				onClose={close}
			/>
		),
		[selectAuthorFilter, selectedAuthor, selectedAuthorId]
	);

	const handleDeleteLog = useCallback(
		async (log: LogView) => {
			const confirmed = await confirm({
				title: 'Excluir log',
				message: 'Tem certeza que deseja excluir este log? Esta ação não pode ser desfeita.',
				confirmText: 'Excluir',
				cancelText: 'Cancelar',
				variant: 'danger',
			});

			if (!confirmed) return;

			try {
				await deleteLog(log.id);
				setSelectedLog(null);
				toastSuccess('Log excluído com sucesso.');
			} catch {
				toastError('Erro ao excluir log. Tente novamente.');
			}
		},
		[confirm, deleteLog, setSelectedLog, toastError, toastSuccess]
	);

	const criticalErrorsLabel =
		typeof criticalErrorsThisMonth === 'number'
			? `${criticalErrorsThisMonth} ${
					criticalErrorsThisMonth === 1 ? 'erro crítico' : 'erros críticos'
				} neste mês`
			: '— erros críticos neste mês';

	if (isLoading) return <LogsPageSkeleton />;

	const tabs: SectionTab<'logs'>[] = [
		{
			key: 'logs',
			label: 'Logs',
			content: (
				<LogsTable
					logs={logs}
					isFetching={isFetching && !isLoading}
					onRowClick={(log: LogView) => setSelectedLog(log)}
				/>
			),
		},
	];

	return (
		<>
			<ListPageLayout
				sidebarWidth="27rem"
				sidebarW1500="22rem"
				sidebarW1300="19rem"
				sidebarW1200="17rem"
				sidebar={
					<>
						<PromotionCard
							title="Entre em contato com a equipe de desenvolvedores"
							subtitle="Poderá ser resolvido caso algum problema surja no Practium"
							buttonText="Contatar"
						/>
						{/* TODO: Connect this report action when a Logs report endpoint exists. */}
						<SystemStatusCard
							healthy={isHealthy}
							isLoading={isMetadataLoading}
							onReportClick={() => {}}
						/>
						<ListPageCharts single>
							<ListPageChartCard>
								{/* TODO: Replace fixed request counts when metadata provides success and total. */}
								<RequestsCard success={7425} total={7426} />
							</ListPageChartCard>
						</ListPageCharts>
					</>
				}
			>
				<SectionCard
					title="Logs do Sistema"
					subtitle="Veja tudo que está sendo processado no sistema"
					headerAction={
						<div className={styles.headerActionStack}>
							<span className={styles.criticalErrorMetric}>{criticalErrorsLabel}</span>
							{/* TODO: Connect report actions when a Logs report endpoint exists. */}
							<ActionButton text="Ver Relatório" onClick={() => {}} />
						</div>
					}
					tabs={tabs}
					activeTab="logs"
					onTabChange={() => {}}
					search={true}
					searchQuery={searchQuery}
					onSearch={setSearchQuery}
					searchPlaceholder="Buscar por tipo ou ação..."
					filterSlot={
						<FilterChip
							values={filterValues}
							onChange={setFilterValues}
							groups={filterGroups}
							placeholder="Filtros"
							customPanels={{
								'periodo:custom': renderPeriodRangePanel,
								'periodo:createdAtUntil': renderCreatedAtUntilPanel,
								'usuario:author': renderUserAuthorPanel,
							}}
						/>
					}
					footer={<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
				/>
			</ListPageLayout>

			{selectedLog && (
				<LogDetailModal
					log={selectedLog}
					onClose={() => setSelectedLog(null)}
					onDelete={() => handleDeleteLog(selectedLog)}
					isDeleting={isDeletingLog}
				/>
			)}
			<ConfirmModal
				isOpen={isConfirmOpen}
				title={confirmOptions.title}
				message={confirmOptions.message}
				confirmText={confirmOptions.confirmText}
				cancelText={confirmOptions.cancelText}
				variant={confirmOptions.variant}
				onConfirm={handleConfirm}
				onCancel={handleConfirmCancel}
			/>
		</>
	);
};
